import { Injectable } from "@angular/core";
import { Observable, forkJoin, map, switchMap, tap } from "rxjs";
import { Launch } from "src/models/launch.model";
import { Instrument, Questionnaire } from "src/models/question.model";
import { ResponseCreateRequest, ResponseUpdateRequest, Response } from "src/models/response.model";
import { LaunchService } from "src/services/launch.service";
import { QuestionService } from "src/services/question.service";
import { ResponseService } from "src/services/response.service";
import { SharedDataService } from "src/services/shared-data.service";
import { DateUtil } from "./shared/utils/date.util";
import { ResponseStatus } from "src/models/app-enums.model";
import { QuestionnaireResponse, QuestionnaireResponseAnswer, QuestionnaireResponseQuestion } from "src/models/questionnaire-response.model";

@Injectable({
    providedIn: 'root'
})
export class AppService {

    culture = $localize.locale;
    
    /**
     * Constructor
     */
    constructor(
        private _launchService: LaunchService,
        private _questionService: QuestionService,
        private _responseService: ResponseService,
        private _sharedDataService: SharedDataService
    ) {}

    /**
     * 
     * @param launchId 
     */
    fetchDataByLaunchId(launchId: string): Observable<{launch: Launch, instrument: Instrument}> {
        return this._launchService.getLaunch(launchId).pipe(
            switchMap((launch: Launch) => {
                return this._questionService.getInstrument(launch.instrumentId, this.culture).pipe(
                    map((instrument) => {
                        return { launch, instrument }
                    })
                )
            })
        )
    }

    /**
     * 
     * @param responseId 
     * @param isPreview 
     * @returns 
     */
    fetchDataByResponseId(responseId: string, isPreview: boolean): Observable<{response: Response, launch: Launch, instrument: Instrument}> {
        return this._responseService.getResponse(responseId, isPreview).pipe(
            switchMap(response => {

                const instrument$ = this._questionService.getInstrument(response.instrumentId, this.culture)
                const launch$ = this._launchService.getLaunch(response.launchId)

                return forkJoin([instrument$, launch$]).pipe(
                    map(([instrument, launch]) => {
                        return {
                            response, launch, instrument
                        }
                    })
                )
            })
        );
    }

    /**
     * Creates a Response, fetches the Final Question, and then updates the Response.
     * Also, combines the Response and Questionnaire into QuestionnaireResponse ( which is the main character of this App )
     * @param responseCreateRequest
     * @param isPreview
     */
    createResponse(responseCreateRequest: ResponseCreateRequest): Observable<QuestionnaireResponse> {

        return this._responseService.createResponse(responseCreateRequest).pipe(
            switchMap((response: Response) => {
                return this._questionService.getQuestionnaire(response.launchId, responseCreateRequest.isPreview).pipe(
                    map((questionnaire: Questionnaire) => {
                        return {
                            response,
                            questionnaire
                        }
                    })
                )
            }),
            switchMap(({ response, questionnaire }) => {
                // capture current time
                const currentDateString: string = DateUtil.toISOString(new Date())
                // construct update request
                const responseUpdateRequest: ResponseUpdateRequest = {
                    responseId: response.responseId,
                    responseStartTime: currentDateString,
                    submitStatus: ResponseStatus.responseInProgress,
                    submitTimeStamp: currentDateString,
                    questions: questionnaire.questions.map(question => {
                        return {
                            questionId: question.questionId,
                            responseText: "",
                            responseType: question.responseType,
                            surveySection: question.surveySection,
                            answerType: question.answerType,
                            answers: []
                        }
                    }),
                    isPreview: responseCreateRequest.isPreview
                }
                // update
                return this._responseService.updateResponse(responseUpdateRequest).pipe(
                    map((response: Response) => {
                        return {
                            response,
                            questionnaire
                        }
                    })
                )
            }),
            map(({ response, questionnaire }) => this.combineQuestionnaireAndResponse(response, questionnaire))
        )
    }

    /**
     * 
     * @param response 
     * @param questionnaire 
     * @returns 
     */
    combineQuestionnaireAndResponse(response: Response, questionnaire: Questionnaire): QuestionnaireResponse {
        const questionnaireResponse: QuestionnaireResponse = {
            ...questionnaire,
            ...response,
            questions: questionnaire.questions.map(question => {
                const questionInResponse = response.questions.find(q => q.questionId === question.questionId)
                const isQuestionAnswered: boolean = questionInResponse?.responseText !== "" || questionInResponse.answers?.some(a => a.selected)
                return {
                    ...question,
                    responseText: questionInResponse?.responseText ?? "",
                    content: question.questionContent.find(content => content.languageCode === $localize.locale)!,
                    isAnswered: isQuestionAnswered,
                    answers: question.answers.map(answer => {
                        const answerInQuestionInResponse = questionInResponse?.answers?.find(a => a.answerId === answer.answerId)
                        return {
                            ...answer,
                            selected: answerInQuestionInResponse ? answerInQuestionInResponse.selected : false,
                            responseTimestamp: questionInResponse?.answers?.find(a => 
                                    a.answerId === answer.answerId)?.responseTimestamp ?? DateUtil.toISOString(new Date()),
                            content: answer.answerContent.find(content => content.languageCode === $localize.locale)!
                        };
                    })
                };
            })
        }
        return questionnaireResponse
    }

    /**
     * 
     * @param questionniareResponse 
     * @returns 
     */
    convertQuestionnaireResponseToResponseUpdateRequest(questionniareResponse: QuestionnaireResponse, isPreview: boolean): ResponseUpdateRequest {
        const responseUpdateRequest: ResponseUpdateRequest = {
            responseId: questionniareResponse.responseId,
            responseStartTime: questionniareResponse.responseStartTime,
            submitStatus: questionniareResponse.submitStatus,
            submitTimeStamp: questionniareResponse.submitStatus === ResponseStatus.responseSubmitted ? DateUtil.toISOString(new Date()) : null,
            questions: questionniareResponse.questions.map((q: QuestionnaireResponseQuestion) => {                
                return {
                    ...q,
                    answers: q.answers.map((a: QuestionnaireResponseAnswer) => {
                        return { ...a }
                    })

                }
            }),
            isPreview: isPreview
        }

        return responseUpdateRequest
    }

}